import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import OpenAPI from '../endpoint/Openapi';

const DEMO_MODE_STORAGE_KEY = 'driverly_demo_mode_active';

interface DemoModeContextType {
  canUseDemoMode: boolean;
  isDemoModeActive: boolean;
  setDemoModeActive: (active: boolean) => void;
  isLoading: boolean;
}

const DemoModeContext = createContext<DemoModeContextType>({
  canUseDemoMode: false,
  isDemoModeActive: false,
  setDemoModeActive: () => {},
  isLoading: true,
});

export const useDemoMode = () => useContext(DemoModeContext);

export const DemoModeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [canUseDemoMode, setCanUseDemoMode] = useState(false);
  const [isDemoModeActive, setIsDemoModeActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch permission from backend on mount
  useEffect(() => {
    const fetchPermission = async () => {
      try {
        const res = await OpenAPI.GET(
          '/api/v2/admin/tenant/me/demo-mode-permission',
          {},
        );
        if (res.data?.canUseDemoMode) {
          setCanUseDemoMode(true);
          // Restore saved state from localStorage
          const savedState = localStorage.getItem(DEMO_MODE_STORAGE_KEY);
          if (savedState === 'true') {
            setIsDemoModeActive(true);
          }
        } else {
          setCanUseDemoMode(false);
          setIsDemoModeActive(false);
          localStorage.removeItem(DEMO_MODE_STORAGE_KEY);
        }
      } catch (error) {
        console.error('Failed to fetch demo mode permission:', error);
        setCanUseDemoMode(false);
        setIsDemoModeActive(false);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchPermission();
  }, []);

  const setDemoModeActive = (active: boolean) => {
    if (!canUseDemoMode) return;

    setIsDemoModeActive(active);
    if (active) {
      localStorage.setItem(DEMO_MODE_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(DEMO_MODE_STORAGE_KEY);
    }
  };

  return (
    <DemoModeContext.Provider
      value={{
        canUseDemoMode,
        isDemoModeActive,
        setDemoModeActive,
        isLoading,
      }}
    >
      {children}
    </DemoModeContext.Provider>
  );
};

export default DemoModeContext;
